import React, { useState } from 'react';

import { IconButton, Popover, Typography } from '@mui/material';
import clsx from 'clsx';
import { CirclePlay, CircleStop, SquarePen } from 'lucide-react';
import { useSnackbar } from 'notistack';
import type { ExistingContractType } from 'src/components';
import { AlineTooltip, QuestionModeIcon, Spacer } from 'src/components';
import { getIsJobLocked, getLockedByTooltip, LockIcon } from 'src/components/reports';
import { QuestionColumnHeaderMenuBody } from 'src/components/reports/report-detail/ColumnHeaderMenu';
import { ExtractionJob, getIsExtractionQuestionRunning } from 'src/editor/toolbars/right-panel/ai';
import { Apollo } from 'src/gql/generated';
import { useCancelExtractionRuns, useRunExtractionJobsForDocuments } from 'src/hooks';
import { Sentry } from 'src/sentry';
import { alineLinkButtonStyles } from 'src/theme';
import type { ReactSetStateTuple, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import type { DocumentWithAiQuestionIdsQueueing } from '../../../document-summary/DocumentSummaryDrawer';

export const useQuestionCardStyles = makeStyles()(theme => ({
  card: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25),
    width: '100%',
  },
  collapseText: {
    ...alineLinkButtonStyles,
    fontSize: 12,
  },
  popover: {
    marginLeft: '-30px',
  },
  popoverPaper: {
    width: 400,
  },
}));

export interface ExtractionJobReview extends Pick<
  Apollo.ExtractionQuestionReview,
  'reviewAction' | 'reviewedAt' | '__typename'
> {
  reviewedBy: UserSubset;
}
interface QuestionCardExtractionJob extends Pick<
  Apollo.ExtractionJob,
  | '__typename'
  | 'id'
  | 'answer'
  | 'createdAt'
  | 'finishedAt'
  | 'failedAt'
  | 'failedReason'
  | 'lockedAt'
  | 'locations'
  | 'reasoning'
  | 'startedAt'
> {
  fileVersion: Pick<Apollo.FileVersion, 'fileContentChangedAt' | 'id' | '__typename'> | null;
  lockedBy: UserSubset | null;
  review: ExtractionJobReview | null;
  selectedOptions: Pick<Apollo.QuestionOption, 'id' | '__typename' | 'label'>[];
}

export interface QuestionCardQuestion extends Pick<
  Apollo.ExtractionQuestion,
  | 'aiGuidance'
  | 'category'
  | 'inputFileFields'
  | 'questionText'
  | 'id'
  | 'title'
  | '__typename'
  | 'type'
  | 'variant'
  | 'options'
  | 'mode'
> {
  contractTypes: ExistingContractType[];
  extractionJob: QuestionCardExtractionJob | null;
  inputs: Pick<Apollo.ExtractionQuestion, 'id' | 'title' | '__typename'>[];
}

interface ExtractionQuestionCardProps {
  document: DocumentWithAiQuestionIdsQueueing;
  editingTuple: ReactSetStateTuple<boolean>;
  onClose: () => void;
  question: QuestionCardQuestion;
  hideEditButton?: boolean;
  hideQuestionText?: boolean;
}

export function ExtractionQuestionCard({
  document,
  editingTuple: [editing, setEditing],
  hideEditButton = false,
  hideQuestionText = false,
  onClose,
  question,
}: ExtractionQuestionCardProps) {
  const { classes } = useQuestionCardStyles();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { cancelExtractionRuns } = useCancelExtractionRuns();
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);
  const isAiQuestionRunning = getIsExtractionQuestionRunning(document, question);
  const { startExtractionJobsForDocuments } = useRunExtractionJobsForDocuments();

  const handlePlayStop = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isAiQuestionRunning) {
      await cancelExtractionRuns(
        { documentIds: [document.id], questionIds: [question.id] },
        { clearRunningJobsOn: { documentId: document.id, questionIds: [question.id] } }
      );
      return;
    }

    await startExtractionJobsForDocuments({
      variables: {
        input: {
          documentIds: [document.id],
          force: true,
          questionIds: [question.id],
        },
      },
    });
  };

  return (
    <>
      <div
        ref={setCardEl}
        className={classes.card}
        data-question-id={question.id}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ExtractionQuestionDisplay
          editingTuple={[editing, setEditing]}
          headerActions={
            <QuestionAnswerLockButton
              extractionJob={question.extractionJob}
              isHovered={isHovered}
            />
          }
          hideEditButton={hideEditButton}
          hideQuestionText={hideQuestionText}
          isHovered={isHovered}
          status={isAiQuestionRunning ? 'running' : 'stopped'}
          values={{
            mode: question.mode,
            questionText: question.questionText,
            title: question.title,
            type: question.type,
          }}
          onClickPlayStop={handlePlayStop}
        />
        <ExtractionJob document={document} question={question} />
      </div>
      <Popover
        anchorEl={cardEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        classes={{
          paper: classes.popoverPaper,
          root: classes.popover,
        }}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        open={editing}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        onClose={onClose}
      >
        <QuestionColumnHeaderMenuBody
          colDef={{
            field: question.id,
            headerName: question.title,
          }}
          context={{
            documentId: document.id,
            extractionJob: question.extractionJob,
            isRunning: isAiQuestionRunning,
            type: 'document',
          }}
          popperAnchor="left-start"
          question={question}
          showHideButton={false}
          onClose={onClose}
        />
      </Popover>
    </>
  );
}

const useQuestionDisplayStyles = makeStyles()(theme => ({
  actionButton: {
    opacity: 0,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  },
  actionButtonVisible: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    minHeight: 32,
  },
  headerGap: {
    gap: theme.spacing(1),
  },
  questionDisplay: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  questionTypeContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  title: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 600,
    padding: 0,
    width: '100%',
  },
}));

function QuestionAnswerLockButton({
  extractionJob,
  isHovered,
}: {
  extractionJob: QuestionCardExtractionJob | null;
  isHovered: boolean;
}) {
  const { classes } = useQuestionDisplayStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [toggleExtractionJobsLock, { loading }] = Apollo.useToggleExtractionJobsLockMutation();

  const isLocked = getIsJobLocked(extractionJob);
  const action = isLocked ? 'unlock' : 'lock';
  const title = isLocked ? `${getLockedByTooltip(extractionJob)}. Click to unlock.` : 'Lock answer';

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!extractionJob) return;
    try {
      await toggleExtractionJobsLock({
        variables: {
          input: {
            extractionJobIds: [extractionJob.id],
            isLocked: action === 'lock',
          },
        },
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'document-question-answer-lock' } });
      enqueueSnackbar('Failed to update answer lock. Please try again.', { variant: 'error' });
    }
  };

  if (!extractionJob?.finishedAt) return null;

  return (
    <AlineTooltip arrow placement="top" title={title}>
      <div>
        <IconButton
          className={clsx(classes.actionButton, isHovered && classes.actionButtonVisible)}
          disabled={loading}
          size="small"
          onClick={handleClick}
        >
          <LockIcon action={action} iconProps={{ size: 18 }} />
        </IconButton>
      </div>
    </AlineTooltip>
  );
}

export interface QuestionCardForm {
  mode: Apollo.ExtractionQuestionMode;
  questionText: string;
  title: string;
  type: Apollo.ExtractionQuestionType;
}

interface ExtractionQuestionDisplayProps<Form extends QuestionCardForm> {
  editingTuple: ReactSetStateTuple<boolean>;
  isHovered: boolean;
  onClickPlayStop: (e: React.MouseEvent) => void;
  status: 'running' | 'stopped';
  values: Form;
  headerActions?: React.ReactNode;
  hideEditButton?: boolean;
  hideQuestionText?: boolean;
}

export function ExtractionQuestionDisplay<T extends QuestionCardForm = QuestionCardForm>({
  editingTuple: [editing, setEditing],
  headerActions,
  hideEditButton = false,
  hideQuestionText,
  isHovered,
  onClickPlayStop,
  status,
  values,
}: ExtractionQuestionDisplayProps<T>) {
  const { classes } = useQuestionDisplayStyles();
  const isAiMode = values.mode === Apollo.ExtractionQuestionMode.Ai;

  return (
    <div className={classes.questionDisplay}>
      <div className={clsx(classes.header, classes.headerGap)}>
        <div className={classes.questionTypeContainer}>
          <QuestionModeIcon mode={values.mode} />
        </div>
        <Typography className={classes.title}>{values.title || 'Untitled'}</Typography>
        <Spacer />
        <div className={classes.header}>
          {headerActions}
          {isAiMode && (
            <AlineTooltip
              arrow
              placement="top"
              title={status === 'running' ? 'Stop question' : 'Run question'}
            >
              <IconButton
                className={clsx(classes.actionButton, isHovered && classes.actionButtonVisible)}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onClickPlayStop(e);
                }}
              >
                {status === 'running' ? <CircleStop size={18} /> : <CirclePlay size={18} />}
              </IconButton>
            </AlineTooltip>
          )}
          {!hideEditButton && (
            <AlineTooltip arrow placement="top" title="Edit question">
              <IconButton
                className={clsx(
                  classes.actionButton,
                  !editing && isHovered && classes.actionButtonVisible
                )}
                color="primary"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setEditing(true);
                }}
              >
                <SquarePen size={18} />
              </IconButton>
            </AlineTooltip>
          )}
        </div>
      </div>
      {!hideQuestionText && <QuestionText questionText={values.questionText} />}
    </div>
  );
}

const useQuestionTextStyles = makeStyles()(theme => ({
  questionText: {
    fontSize: 14,
    padding: 0,
    width: '100%',
  },
  questionTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(1),
  },
}));

interface QuestionTextProps {
  questionText: string;
}

function QuestionText({ questionText }: QuestionTextProps): JSX.Element {
  const { classes } = useQuestionTextStyles();

  if (!questionText)
    return (
      <div className={classes.questionTextContainer}>
        <Typography>No question text</Typography>
      </div>
    );

  const lines = questionText.split('\n');

  return (
    <div className={classes.questionTextContainer}>
      {lines.map((line, index) => (
        <Typography key={index} className={classes.questionText}>
          {line || '\u200b'}
        </Typography>
      ))}
    </div>
  );
}
